import { useRef, useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme, Skeleton } from "@mui/material";
import { alpha } from "@mui/material/styles";
const EvidenceSetCard = ({ check, isSelected, onClick }) => {
  const theme = useTheme();
  const cardRef = useRef(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionText = useMemo(
    () => check?.activeCheckDescription || check?.checkDescription || "No description available",
    [check?.activeCheckDescription, check?.checkDescription]
  );
  const shouldShowToggle = descriptionText.length > 65;

  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [isSelected]);

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [check?.checkType, check?.activeCheckName]);

  return (
    <Box
      ref={cardRef}
      onClick={onClick}
      
      sx={{
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.05)
          : "#FFFFFF",
        border: `1px solid ${
          isSelected
            ? theme.palette.primary.main
            : alpha(theme.customColors.border.main, 0.3)
        }`,
        borderRadius: "8px",
        p: 2.5,
        pb: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: isSelected ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none",
        "&:hover": {
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
          borderColor: isSelected
            ? theme.palette.primary.main
            : alpha(theme.customColors.border.main, 0.5),
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.08)
            : "#FFFFFF",
        },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color={theme.customColors.text.strong}
        sx={{ pb: 2, lineHeight: 1.4 }}
      >
        {check?.activeCheckName?.replace(/_/g, " ") || "UNKNOWN"}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8125rem",
          color: theme.customColors.text.secondary,
          lineHeight: 1.5,
          mb: shouldShowToggle ? 0.75 : 2,
          display: "-webkit-box",
          WebkitLineClamp: isDescriptionExpanded ? "unset" : 2,
          WebkitBoxOrient: "vertical",
          overflow: isDescriptionExpanded ? "visible" : "hidden",
        }}
      >
        {descriptionText}
      </Typography>
      {shouldShowToggle && (
        <Typography
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setIsDescriptionExpanded((prev) => !prev);
          }}
          sx={{
            border: "none",
            background: "transparent",
            p: 0,
            m: 0,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: theme.palette.primary.main,
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {isDescriptionExpanded ? "Show less" : "Show more"}
        </Typography>
      )}
    </Box>
  );
};

const EvidenceSetSidebar = ({ checks, selectedCheckType, onCheckSelect, loading }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "300px",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          pt: 1.5,
          pb: 1,
          borderBottom: `1px solid ${alpha(theme.customColors.border.main, 0.6)}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            pl: 1,
            fontSize: "0.8125rem",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            color: theme.customColors.text.secondary,
          }}
        >
          Checks
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          pt: 1.25,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.customColors.border.main, 0.7),
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: alpha(theme.customColors.border.main, 0.7),
            },
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {[1, 2, 3].map((index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  backgroundColor: "#FFFFFF",
                  border: `1px solid ${alpha(theme.customColors.border.main, 0.3)}`,
                  borderRadius: "8px",
                  p: 2.5,
                  pb: 2,
                }}
              >
                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1.5 }} />
                <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="90%" height={16} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {checks.map((check, index) => {
              const checkType = check.checkType || check.activeCheckName;
              const isSelected = checkType === selectedCheckType;

              return (
                <Box
                  key={`check-${index}`}
                  sx={{
                    borderBottom:
                      index < checks.length - 1
                        ? `1px solid ${theme.customColors.border.main}`
                        : "none",
                    pb: index < checks.length - 1 ? 1.25 : 0,
                    mb: index < checks.length - 1 ? 1.25 : 0,
                  }}
                >
                  <EvidenceSetCard
                    check={check}
                    isSelected={isSelected}
                    onClick={() => onCheckSelect(check)}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EvidenceSetSidebar;
